import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useGetTreatment, useSplitContext } from '@zg-rentals/split';

import SplitTestConstants from '../constants/SplitTestConstants';
import accessUtils from '../utils/accessUtils';

/**
 * Determines access to renters insurance form field
 *
 * The response rely's on a few async api responses
 *
 * @returns { getTreatmentError, isGetTreatmentError, isGetTreatmentLoading, canAccessRentersInsuranceFormField }
 */
const useAccessRentersInsurance = () => {
  const {
    split: { experiments: splitExperiments },
  } = useSplitContext();

  const {
    // @ts-expect-error
    app: { cookieEnabledServices },
    // @ts-expect-error
    properties: {
      currentProperty: {
        listingAlias,
        rentersInsurance: { isEligibleForIsRentersInsuranceRequiredQuestion },
      },
    },
  } = useSelector((state) => state);

  const [canAccessRentersInsuranceFormField, setCanAccessRentersInsuranceFormField] = useState<boolean>(false);

  /**
   * NOTE: must be called before `accessUtils` checks
   */
  const {
    data: getTreatmentData,
    error: getTreatmentError,
    isError: isGetTreatmentError,
    isFetching: isGetTreatmentFetching,
    isLoading: isGetTreatmentLoading,
    refetch: refetchGetTreatment,
  } = useGetTreatment(
    {
      featureName: SplitTestConstants.ZRM_RENTERS_INSURANCE_LISTING_PATH,
      listingAlias,
    },
    {
      enabled: false,
    },
  );

  // splitExperiments needs to have the experiment name as a property in order to check the treatment value
  const splitExperimentsHasRentersInsuranceProperty = splitExperiments.hasOwnProperty(
    SplitTestConstants.ZRM_RENTERS_INSURANCE_LISTING_PATH,
  );

  useEffect(() => {
    if (isEligibleForIsRentersInsuranceRequiredQuestion && !getTreatmentData && isGetTreatmentFetching === false) {
      refetchGetTreatment();
    }
  }, [isEligibleForIsRentersInsuranceRequiredQuestion]);

  useEffect(() => {
    if (isGetTreatmentLoading === false && splitExperimentsHasRentersInsuranceProperty) {
      const canAccessResponse = accessUtils.canAccessRentersInsuranceFormField({
        cookieEnabledServices,
        isEligibleForIsRentersInsuranceRequiredQuestion,
        splitExperiments,
      });

      setCanAccessRentersInsuranceFormField(canAccessResponse);
    }
  }, [isGetTreatmentLoading, splitExperimentsHasRentersInsuranceProperty]);

  return { getTreatmentError, isGetTreatmentError, isGetTreatmentLoading, canAccessRentersInsuranceFormField };
};

export default useAccessRentersInsurance;
